import React, { useMemo, useState } from 'react';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import {
  OWNERSHIP_TYPE,
  LOCATION_TYPE,
  SPONSORED_BY,
  STATION_TYPE,
  SCHOOL_BUILDING,
  INFRA_FACILITY,
  PM_SHRI_PHASE,
  INTERNET_PLAN,
  INTERNET_CONNECTIVITY,
  CONTACT_ROLE,
  INFRA_SIZE_TYPE,
  INTERNET_TYPE,
  NCC_GROUP,
  NCC_GROUP_WING,
  NCC_GROUP_ANO_CTO,
  NCC_ANO_CTO_RANK,
  SPORT_CATEGORY,
  SPORT_TYPE,
  SPORT_TITLE,
  SPORT_QUALITY,
  SPORT_MEASURE_UNIT,
  BUILDING_TYPE,
  INFRA_NAME,
  LAB_FUNDED_BY
} from '../constants/lookupMaps';

interface ChartDataItem {
  name: string;
  value: number;
  [key: string]: any;
}

interface AdditionalDataset {
  label: string;
  data: number[];
  backgroundColor?: string;
  borderColor?: string;
  borderWidth?: number;
}

interface ChartViewProps {
  data: ChartDataItem[];
  chartType?: 'bar' | 'pie' | 'line' | 'area';
  title?: string;
  xAxisLabel?: string;
  yAxisLabel?: string;
  dataKey?: string;
  nameKey?: string;
  kpiField?: string; // The actual KPI field name for formatting (e.g., 'ownership', 'sponsored')
  additionalDatasets?: AdditionalDataset[];
  customOptions?: {
    legend?: {
      display?: boolean;
      position?: 'top' | 'bottom' | 'left' | 'right';
    };
  };
}

// Utility function to format values using lookup maps (same as in school.tsx)
const formatCellValue = (value: any, key?: string): string => {
  if (value === null || value === undefined) {
    return '-';
  }

  // Helper: normalize key variants for lookup
  const normalize = (k?: string) => (k || '').replace(/\s+/g, '').replace(/\./g, '').toLowerCase();

  // Lookup mapping table by normalized key -> mapping object
  const lookupByKey: Record<string, Record<string, string> | undefined> = {
    // Ownership
    ownership: OWNERSHIP_TYPE,
    ownershiptype: OWNERSHIP_TYPE,

    // Location
    locationtype: LOCATION_TYPE,
    location: LOCATION_TYPE,

    // Sponsored
    sponsoredby: SPONSORED_BY,
    sponsored: SPONSORED_BY,

    // Station
    stationtype: STATION_TYPE,

    // Building
    schoolbuilding: SCHOOL_BUILDING,
    buildingtype: BUILDING_TYPE,

    // Infra facility
    infrafacility: INFRA_FACILITY,

    // PM Shri phase
    ispmshree: PM_SHRI_PHASE,
    pmshri: PM_SHRI_PHASE,
    pmshriphase: PM_SHRI_PHASE,

    // Internet plan/type
    internetplan: INTERNET_PLAN,
    internetype: INTERNET_TYPE,
    internetconnectivity: INTERNET_CONNECTIVITY,

    // Contact role
    contactrole: CONTACT_ROLE,
    role: CONTACT_ROLE,

    // Infra size
    infrasizetype: INFRA_SIZE_TYPE,
    sizetype: INFRA_SIZE_TYPE,

    // NCC
    nccgroup: NCC_GROUP,
    nccgroupwing: NCC_GROUP_WING,
    nccgroupanocto: NCC_GROUP_ANO_CTO,
    nccanoctorank: NCC_ANO_CTO_RANK,

    // Sports
    sporttype: SPORT_TITLE,
    sporttitle: SPORT_TITLE,
    sportstitle: SPORT_TITLE,
    sportcategory: SPORT_CATEGORY,
    quality: SPORT_QUALITY,
    measurementunit: SPORT_MEASURE_UNIT,

    // Infrastructure
    name: INFRA_NAME,
    infraname: INFRA_NAME,

    // ATL Lab
    fundedby: LAB_FUNDED_BY,
    funded: LAB_FUNDED_BY,
  };

  // If we have a mapping for this key, return mapped value when possible
  if (key) {
    const nk = normalize(key);
    const map = lookupByKey[nk];
    if (map) {
      const mapped = (map as any)[String(value)];
      if (mapped !== undefined) return String(mapped);
    }
  }

  // Special handling for isPMShree field - it represents phases
  const isPMShreeField = key === 'isPMShree';
  if (isPMShreeField) {
    const numValue = Number(value);
    if (numValue === 0) return 'Non PM Shri';
    if (numValue >= 1) return `Phase ${numValue}`;
    return String(value);
  }

  // Check if the key name indicates it's a boolean field (Yes/No)
  const isBooleanField = key ?
    /^is[A-Z]/.test(key) ||
    key.toLowerCase().endsWith('status') ||
    key.toLowerCase().endsWith('active') ||
    key.toLowerCase().endsWith('available') :
    false;

  // Only convert 0/1 to Yes/No if it's a boolean field
  if (isBooleanField && (value === 0 || value === 1 || value === '0' || value === '1')) {
    return value === 1 || value === '1' ? 'Yes' : 'No';
  }

  return String(value);
};

// Color palette matching your theme
const COLORS = [
  '#6B1786', // Primary purple
  '#481789', // Dark purple
  '#8E24AA', // Medium purple
  '#AB47BC', // Light purple
  '#BA68C8', // Lighter purple
  '#CE93D8', // Very light purple
  '#E1BEE7', // Pastel purple
  '#F3E5F5', // Very light lavender
  '#4A148C', // Deep purple
  '#7B1FA2', // Purple variant
  '#9C27B0', // Material purple
  '#673AB7', // Deep purple variant
];

const ChartView: React.FC<ChartViewProps> = ({
  data,
  chartType = 'bar',
  title,
  xAxisLabel = 'Category',
  yAxisLabel = 'Count',
  dataKey = 'value',
  nameKey = 'name',
  kpiField,
  additionalDatasets = [],
  customOptions = {},
}) => {
  const [selectedChartType, setSelectedChartType] = useState(chartType);

  // Transform and sort data for charts
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    const transformed = data.map((item) => {
      const rawName = item[nameKey] || item.name || 'Unknown';
      // Format the name using the lookup maps if kpiField is provided
      const formattedName = kpiField ? formatCellValue(rawName, kpiField) : rawName;
      
      return {
        name: formattedName,
        rawName: rawName, // Keep raw value for reference
        value: Number(item[dataKey] || item.value || 0),
        sortKey: item.sortKey, // Optional numeric sort key
      };
    });
    
    // Sort by sortKey if available (for numeric ordering), otherwise maintain input order
    if (transformed.some(item => item.sortKey !== undefined)) {
      return transformed.sort((a, b) => (a.sortKey ?? 0) - (b.sortKey ?? 0));
    }
    
    // Return in original order (don't sort lexographically)
    return transformed;
  }, [data, nameKey, dataKey, kpiField]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-800 mb-1">{payload[0].payload.name}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-purple-600 font-medium" style={{ color: entry.color }}>
              {entry.name}: {entry.value.toLocaleString()}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Render label for pie chart with smart positioning for small slices
  const renderLabel = (entry: any) => {
    const percent = ((entry.value / chartData.reduce((sum, item) => sum + item.value, 0)) * 100);
    
    // Only show label if slice is >= 1% to avoid clutter on tiny slices
    if (percent < 1) {
      return '';
    }
    
    // For slices between 1-2%, show just percentage
    if (percent < 2) {
      return `${percent.toFixed(1)}%`;
    }
    
    // For larger slices, show name and percentage
    const displayName = entry.name.length > 15 ? entry.name.substring(0, 15) + '...' : entry.name;
    return `${displayName}: ${percent.toFixed(1)}%`;
  };

  // Custom label with improved positioning and alternating near/far pattern
  const renderCustomLabel = (props: any) => {
    const { cx, cy, midAngle, outerRadius, fill, percent, name, index } = props;
    const RADIAN = Math.PI / 180;
    
    // Show ALL labels - no hiding even for tiny slices
    const displayPercent = (percent * 100).toFixed(1);
    
    // Alternate between near and far positioning for better spacing
    // Even indices: closer to pie, Odd indices: farther from pie
    const isEven = index % 2 === 0;
    let radius;
    
    if (percent < 0.01) {
      // Very tiny slices: extreme alternation to avoid overlap
      radius = isEven ? outerRadius + 55 : outerRadius + 85;
    } else if (percent < 0.02) {
      // Small slices: strong alternation
      radius = isEven ? outerRadius + 45 : outerRadius + 70;
    } else if (percent < 0.05) {
      // Medium-small slices: moderate alternation
      radius = isEven ? outerRadius + 35 : outerRadius + 55;
    } else {
      // Larger slices: subtle alternation
      radius = isEven ? outerRadius + 28 : outerRadius + 45;
    }
    
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    
    // Truncate long names to fit within container
    const maxNameLength = percent < 0.02 ? 8 : 18;
    const displayName = name.length > maxNameLength ? name.substring(0, maxNameLength) + '...' : name;
    
    return (
      <text
        x={x}
        y={y}
        fill="#333"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize={percent < 0.015 ? '10px' : '12px'}
        fontWeight="500"
      >
        {percent >= 0.015 ? `${displayName}: ${displayPercent}%` : `${displayPercent}%`}
      </text>
    );
  };

  if (!chartData || chartData.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-8">
        <div className="text-center text-gray-400 py-12">
          No data available for chart visualization
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      {/* Header with title and chart type selector */}
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold text-gray-800">
          {title || 'Data Visualization'}
        </h3>
        <div className="flex gap-2">
          <button
            onClick={() => setSelectedChartType('bar')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              selectedChartType === 'bar'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            title="Bar Chart"
          >
            📊 Bar
          </button>
          <button
            onClick={() => setSelectedChartType('line')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              selectedChartType === 'line'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            title="Line Chart"
          >
            📈 Line
          </button>
          <button
            onClick={() => setSelectedChartType('area')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              selectedChartType === 'area'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            title="Area Chart"
          >
            📉 Area
          </button>
          <button
            onClick={() => setSelectedChartType('pie')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              selectedChartType === 'pie'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            title="Pie Chart"
          >
            🥧 Pie
          </button>
        </div>
      </div>

      {/* Chart Container */}
      <div className="w-full" style={{ minHeight: '500px' }}>
        {selectedChartType === 'bar' && (() => {
          // Prepare data with additional datasets
          const enrichedData = chartData.map((item, index) => {
            const enriched: any = { ...item };
            additionalDatasets.forEach((dataset, dsIndex) => {
              enriched[`dataset_${dsIndex}`] = dataset.data[index] || 0;
            });
            return enriched;
          });

          return (
            <ResponsiveContainer width="100%" height={600}>
              <BarChart data={enrichedData} margin={{ top: 20, right: 30, left: 20, bottom: 140 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="name"
                  angle={-45}
                  textAnchor="end"
                  height={50}
                  interval={0}
                  tick={{ fontSize: 12, fill: '#666', dy: 30 }}
                  label={{ value: xAxisLabel, position: 'insideBottom', style: { fill: '#666', fontWeight: 500, textAnchor: 'middle' }, dy: 120 }}
                />
                <YAxis 
                  tick={{ fontSize: 12, fill: '#666' }}
                  label={{ value: yAxisLabel, angle: -90, position: 'insideLeft', style: { fill: '#666', fontWeight: 500 } }}
                />
                <Tooltip content={<CustomTooltip />} />
                {(customOptions.legend?.display !== false) && (
                  <Legend 
                    wrapperStyle={{ marginTop: '20px' }}
                    verticalAlign={customOptions.legend?.position === 'bottom' ? 'bottom' : 'top'}
                  />
                )}
                <Bar dataKey="value" name={yAxisLabel || "Records"} fill="rgba(54, 162, 235, 0.6)" radius={[8, 8, 0, 0]} />
                {additionalDatasets.map((dataset, index) => (
                  <Bar 
                    key={`dataset-${index}`}
                    dataKey={`dataset_${index}`}
                    name={dataset.label}
                    fill={dataset.backgroundColor || COLORS[(index + 1) % COLORS.length]}
                    radius={[8, 8, 0, 0]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          );
        })()}

        {selectedChartType === 'line' && (
          <ResponsiveContainer width="100%" height={500}>
            <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="name"
                angle={-45}
                textAnchor="end"
                height={100}
                interval={0}
                tick={{ fontSize: 12, fill: '#666', dy: 30 }}
                label={{ value: xAxisLabel, offset: -10, position: 'insideBottom', style: { fill: '#666', fontWeight: 500 } }}
              />
              <YAxis 
                tick={{ fontSize: 12, fill: '#666' }}
                label={{ value: yAxisLabel, angle: -90, position: 'insideLeft', style: { fill: '#666', fontWeight: 500 } }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ paddingTop: '20px' }} />
              <Line
                type="monotone"
                dataKey="value"
                name={yAxisLabel}
                stroke="#6B1786"
                strokeWidth={3}
                dot={{ fill: '#6B1786', r: 5 }}
                activeDot={{ r: 7 }}
                animationDuration={1200}
                animationEasing="ease-in-out"
              />
            </LineChart>
          </ResponsiveContainer>
        )}

        {selectedChartType === 'area' && (
          <ResponsiveContainer width="100%" height={500}>
            <AreaChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
              <defs>
                <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6B1786" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#6B1786" stopOpacity={0.1} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="name"
                angle={-45}
                textAnchor="end"
                height={100}
                interval={0}
                tick={{ fontSize: 12, fill: '#666', dy: 30 }}
                label={{ value: xAxisLabel, offset: -10, position: 'insideBottom', style: { fill: '#666', fontWeight: 500 } }}
              />
              <YAxis 
                tick={{ fontSize: 12, fill: '#666' }}
                label={{ value: yAxisLabel, angle: -90, position: 'insideLeft', style: { fill: '#666', fontWeight: 500 } }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ paddingTop: '20px' }} />
              <Area
                type="monotone"
                dataKey="value"
                name={yAxisLabel}
                stroke="#6B1786"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorValue)"
                animationDuration={1200}
                animationEasing="ease-in-out"
              />
            </AreaChart>
          </ResponsiveContainer>
        )}

        {selectedChartType === 'pie' && (
          <ResponsiveContainer width="100%" height={750}>
            <PieChart margin={{ top: 40, right: 80, bottom: 60, left: 80 }}>
              <Pie
                data={chartData}
                cx="50%"
                cy="45%"
                labelLine={{
                  stroke: '#999',
                  strokeWidth: 1,
                }}
                label={renderCustomLabel}
                outerRadius={200}
                innerRadius={65}
                fill="#6B1786"
                dataKey="value"
                animationDuration={1000}
                animationEasing="ease-out"
                paddingAngle={1}
                minAngle={2}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend
                verticalAlign="bottom"
                height={50}
                wrapperStyle={{ paddingTop: '30px', fontSize: '12px' }}
                iconSize={10}
              />
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Summary Statistics */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center">
            <p className="text-sm text-gray-500">Total Items</p>
            <p className="text-2xl font-bold text-purple-600">{chartData.length}</p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-500">Total Count</p>
            <p className="text-2xl font-bold text-purple-600">
              {chartData.reduce((sum, item) => sum + item.value, 0).toLocaleString()}
            </p>
          </div>
          {/* <div className="text-center">
            <p className="text-sm text-gray-500">Average</p>
            <p className="text-2xl font-bold text-purple-600">
              {(chartData.reduce((sum, item) => sum + item.value, 0) / chartData.length).toFixed(0)}
            </p>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default ChartView;
